import { CSSProperties, useEffect, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { useMediaQuery } from "react-responsive";
import Icon from "../Icon";
import useIsMounted from "../../hooks/useIsMounted";
import classNames from "classnames";
import { Asset } from "../../types/Asset";
import "./style.scss";

const ImageOrVideo = ({
  image,
  caption = "",
  keepAspectRatio = false,
  centerImage = false,
  containImage = false,
  style = {},
  imageStyle = {},
  className = "",
  preferredStyles = []
}: {
  image: Asset;
  caption?: string;
  keepAspectRatio?: boolean;
  centerImage?: boolean;
  containImage?: boolean;
  style?: CSSProperties;
  imageStyle?: CSSProperties;
  className?: string;
  preferredStyles?: string[];
}) => {
  const isMounted = useIsMounted();
  const isPrint = useMediaQuery({ query: "print" });
  const [imageLoaded, setImageLoaded] = useState(false);

  let imageSize = "px1920";
  let altStyle = "medium";
  if (
    useMediaQuery({
      query:
        "(-webkit-min-device-pixel-ratio: 2) and (max-width: 575.98px), (-webkit-max-device-pixel-ratio: 1.99) and (max-width: 991.98px), (min-resolution: 2dppx) and (max-width: 575.98px), (max-resolution: 1.99dppx) and (max-width: 991.98px)"
    })
  ) {
    imageSize = "px828";
    altStyle = "preview";
  }
  if (
    useMediaQuery({
      query:
        "(-webkit-min-device-pixel-ratio: 2) and (min-width: 1400px), (-webkit-max-device-pixel-ratio: 1.99) and (min-width: 2400px), (min-resolution: 2dppx) and (min-width: 1400px), (max-resolution: 1.99dppx) and (min-width: 2400px)"
    })
  ) {
    imageSize = "px3840";
    altStyle = "medium";
  }
  preferredStyles.push(imageSize);
  preferredStyles.push(altStyle);
  let imageSrc;
  for (const imageStyle of preferredStyles) {
    if (image?.styles?.[imageStyle]) {
      imageSrc = image.styles[imageStyle];
      break;
    }
  }
  if (!imageSrc) {
    imageSrc = image?.path;
  }
  const mayBeTransparentImage = imageSrc
    ? !!imageSrc.match(/\.png/) ||
      !!imageSrc.match(/\.gif/) ||
      !!imageSrc.match(/\.svg/)
    : false;

  useEffect(() => {
    if (imageSrc && !image?.video) {
      const loader = document.createElement("img");
      loader.onload = () => {
        if (isMounted()) setImageLoaded(true);
      };
      loader.src = imageSrc;
      return () => {
        loader.onload = null;
      };
    }
  }, [image?.video, imageSrc, isMounted]);

  //console.log("image", image);

  return image && imageSrc ? (
    !image.video ? (
      <div
        className={classNames("image-container", className)}
        style={{
          ...(keepAspectRatio && {
            position: "relative",
            height: "100%",
            width: "auto"
          }),
          ...(!mayBeTransparentImage &&
            !containImage && {
              background: image.colors
                ? `linear-gradient(32deg, ${image.colors[0]} 0%, ${image.colors[1]} 100%)`
                : `linear-gradient(32deg, #dfe2e1 0%, #d0d2d1 100%)`
            }),
          ...style
        }}
      >
        {!isPrint ? (
          <AnimatePresence>
            <motion.img
              initial={{ opacity: imageLoaded ? 1 : 0 }}
              animate={{ opacity: imageLoaded ? 1 : 0 }}
              transition={{ duration: 0.25 }}
              src={imageSrc}
              alt={caption}
              style={{
                display: "block",
                ...(keepAspectRatio
                  ? {
                      height: "100%",
                      maxWidth: "100%"
                    }
                  : {
                      position: "absolute",
                      top: 0,
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: "100%",
                      width: "100%"
                    }),
                objectFit: containImage ? "contain" : "cover",
                objectPosition: centerImage ? "center center" : "center bottom",
                ...imageStyle
              }}
            />
          </AnimatePresence>
        ) : (
          <img
            src={imageSrc}
            alt={caption}
            style={{
              display: "block",
              position: "absolute",
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
              height: "100%",
              width: "100%",
              objectFit: containImage ? "contain" : "cover",
              objectPosition: centerImage ? "center center" : "center bottom",
              ...imageStyle
            }}
          />
        )}
      </div>
    ) : image.video ? (
      <video
        muted
        playsInline
        autoPlay
        loop
        src={image.path}
        style={{
          display: "block",
          position: "absolute",
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
          height: "100%",
          width: "100%",
          objectFit: containImage ? "contain" : "cover",
          objectPosition: "center center",
          ...style
        }}
        className={className}
      >
        <p>{caption}</p>
      </video>
    ) : (
      <div
        className={classNames("image-container", "empty-msg", className)}
        style={{
          background: "#ccc"
        }}
      >
        <div>
          <Icon name="face-sad-tear" size="4x" />
        </div>
        <div>Unsupported image/video format</div>
      </div>
    )
  ) : (
    <div
      className={classNames("image-container", "empty-msg", className)}
      style={{
        background: "#ccc"
      }}
    >
      <div>
        <Icon name="face-sad-tear" size="4x" />
      </div>
      <div>No image selected</div>
    </div>
  );
};

export default ImageOrVideo;
